import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { IconButton } from "@/components/ui/icon-button";
import { Button } from "@/components/ui/button";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  Share2,
  MapPin,
  Calendar,
  Tag,
  FileText,
  ZoomIn,
  ZoomOut,
  Brain,
  Leaf,
  Sprout,
  Lightbulb,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Photo } from "./PhotoGrid";

export interface PhotoDetailViewProps {
  photos: Photo[];
  currentIndex: number;
  onClose: () => void;
  onEdit?: (photo: Photo) => void;
  onDelete?: (photo: Photo) => void;
  onShare?: (photo: Photo) => void;
  className?: string;
}

export const PhotoDetailView: React.FC<PhotoDetailViewProps> = ({
  photos,
  currentIndex: initialIndex,
  onClose,
  onEdit,
  onDelete,
  onShare,
  className,
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [showMetadata, setShowMetadata] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);

  const currentPhoto = photos[currentIndex];
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < photos.length - 1;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrevious) handlePrevious();
      if (e.key === "ArrowRight" && hasNext) handleNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, photos.length]);

  const handlePrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(currentIndex - 1);
      setZoom(1);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1);
      setZoom(1);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;

    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0 && hasNext) {
        handleNext();
      } else if (diff < 0 && hasPrevious) {
        handlePrevious();
      }
    }

    setTouchStart(null);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 1));
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 bg-black flex flex-col",
        className
      )}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-10 pt-[env(safe-area-inset-top)] p-4 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between">
          <IconButton
            icon={X}
            variant="ghost"
            size="md"
            onClick={onClose}
            className="text-white hover:bg-white/20"
          />
          <div className="flex items-center gap-2">
            <Badge variant="default" className="bg-white/20 text-white border-0">
              {currentIndex + 1} / {photos.length}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              icon={ZoomOut}
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              disabled={zoom <= 1}
              className="text-white hover:bg-white/20"
            />
            <IconButton
              icon={ZoomIn}
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              disabled={zoom >= 3}
              className="text-white hover:bg-white/20"
            />
          </div>
        </div>
      </div>

      {/* Main Image Area */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Navigation Buttons */}
        {hasPrevious && (
          <button
            onClick={handlePrevious}
            className="absolute left-4 z-10 w-12 h-12 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {hasNext && (
          <button
            onClick={handleNext}
            className="absolute right-4 z-10 w-12 h-12 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        {/* Image */}
        <img
          src={currentPhoto.url}
          alt={currentPhoto.category}
          className="max-w-full max-h-full object-contain transition-transform duration-200"
          style={{ transform: `scale(${zoom})` }}
          onClick={() => setShowMetadata(!showMetadata)}
        />
      </div>

      {/* Bottom Metadata Panel */}
      {showMetadata && (
        <div className="absolute bottom-0 left-0 right-0 z-10 pb-[env(safe-area-inset-bottom)]">
          <Card
            elevation="high"
            className="rounded-t-3xl rounded-b-none p-6 bg-card/95 backdrop-blur-lg"
          >
            {/* Category Badge */}
            <Badge variant="default" size="lg" className="mb-4">
              {currentPhoto.category}
            </Badge>

            {/* Metadata Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              {currentPhoto.date && (
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Date</p>
                    <p className="text-sm font-medium text-foreground">
                      {currentPhoto.date}
                    </p>
                  </div>
                </div>
              )}

              {currentPhoto.location && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Location</p>
                    <p className="text-sm font-medium text-foreground">
                      {currentPhoto.location}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* AI Analysis Panel */}
            {currentPhoto.aiAnalysis?.for_farmer && (
              <div className="mb-4 p-4 rounded-xl bg-muted/50 border border-border space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Brain className="w-4 h-4 text-primary" />
                  {t('photo.aiAnalysis', { defaultValue: 'AI Analysis' })}
                </div>

                {currentPhoto.aiAnalysis.for_farmer.status && (
                  <div className="flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-primary" />
                    <span className="text-sm">{t('photo.health', { defaultValue: 'Health' })}:</span>
                    <span className={
                      currentPhoto.aiAnalysis.for_farmer.status === 'excellent' || currentPhoto.aiAnalysis.for_farmer.status === 'good'
                        ? 'text-sm font-medium text-success capitalize'
                        : currentPhoto.aiAnalysis.for_farmer.status === 'fair'
                        ? 'text-sm font-medium text-warning capitalize'
                        : 'text-sm font-medium text-destructive capitalize'
                    }>
                      {currentPhoto.aiAnalysis.for_farmer.status}
                    </span>
                  </div>
                )}

                {currentPhoto.aiAnalysis.for_farmer.main_finding && (
                  <div className="flex items-start gap-2">
                    <Sprout className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <span className="text-sm">{currentPhoto.aiAnalysis.for_farmer.main_finding}</span>
                  </div>
                )}

                {currentPhoto.aiAnalysis.for_farmer.action && currentPhoto.aiAnalysis.for_farmer.action !== 'none' && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/5 border border-primary/20">
                    <Lightbulb className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-sm">
                      {currentPhoto.aiAnalysis.for_farmer.action === 'see_agriagent' && t('camera.actionAgriAgent', { defaultValue: 'Ask your AgriAgent for advice' })}
                      {currentPhoto.aiAnalysis.for_farmer.action === 'see_community' && t('camera.actionCommunity', { defaultValue: 'Check the community network' })}
                      {currentPhoto.aiAnalysis.for_farmer.action === 'contact_supervisor' && t('camera.actionSupervisor', { defaultValue: 'Contact your supervisor' })}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(currentPhoto)}
                  icon={Edit}
                >
                  Edit
                </Button>
              )}
              {onShare && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onShare(currentPhoto)}
                  icon={Share2}
                >
                  Share
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onDelete(currentPhoto)}
                  icon={Trash2}
                  className="ml-auto"
                >
                  Delete
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
